import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { num1 } from '../../lib/numeros';
import { conCantidadConNombre } from '../../lib/cantidadConNombre';

/**
 * «SUGIÉREME UN POSTENTRENO» (Jesús, artefacto «El postentreno, cerrado», 27-09).
 *
 * «Como una comida normal de la calculadora. Y al dar a sugerir: primero los nuestros,
 * después los de otros usuarios, igual que salen los favoritos en las demás comidas.» Los de
 * otros usuarios «no son otra pantalla: van debajo de los nuestros, en la misma lista».
 *
 * Los nuestros llegan ya ajustados a los macros del post (core/post_nuestros); los de otros
 * usuarios salen de la biblioteca de siempre, también ajustados.
 *
 * Audio de Jesús (27-09, 22:34): «tiene que salir un botón de mostrar más: no se ven los de
 * otros usuarios». De los nuestros salen NUESTROS_A_LA_VISTA y el resto tras «Mostrar más»,
 * para que «De otros usuarios» asome sin bajar. Y ya ninguno sale como promocionado.
 */
const NUESTROS_A_LA_VISTA = 2;
const cantidad = (a) => (a.al_gusto ? 'al gusto' : a.texto_cantidad
    || `${num1(a.cantidad_g)} ${a.es_liquido ? 'ml' : 'g'}`);

function Receta({ r, onUsar }) {
    const [abierta, setAbierta] = useState(false);
    return (
        <div className="rounded-xl border border-border p-3" data-testid={`post-receta-${r.id}`}>
            <button type="button" onClick={() => setAbierta((v) => !v)} className="w-full text-left">
                <span className="block font-semibold text-foreground text-sm">{r.titulo}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{r.descripcion}</span>
            </button>
            {abierta && (
                <ul className="mt-2 space-y-1" data-testid={`post-receta-${r.id}-alimentos`}>
                    {r.alimentos.map((a, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 text-xs">
                            <span className="text-foreground min-w-0">{a.nombre}{a.fijo ? ' · fijo' : ''}</span>
                            <span className="text-muted-foreground whitespace-nowrap">{cantidad(a)}</span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground tabular-nums">
                    P {num1(r.macros?.P || 0)} · H {num1(r.macros?.H || 0)}
                </span>
                <span className="flex gap-1.5">
                    <button type="button" onClick={() => setAbierta((v) => !v)}
                        className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-foreground">
                        {abierta ? 'Ocultar' : 'Ver'}
                    </button>
                    <button type="button" onClick={() => onUsar(r)} data-testid={`post-usar-${r.id}`}
                        className="px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-bold hover:opacity-90">
                        Usar
                    </button>
                </span>
            </div>
        </div>
    );
}

export default function PostSugeridos({ open, onClose, api, objetivo, onUsar }) {
    const [nuestros, setNuestros] = useState(null);
    const [otros, setOtros] = useState(null);
    const [todos, setTodos] = useState(false);
    const [todosNuestros, setTodosNuestros] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelado = false;
        setNuestros(null); setOtros(null); setTodos(false); setTodosNuestros(false);
        const P = Math.max(0, objetivo?.P || 0);
        const H = Math.max(0, objetivo?.H || 0);
        api(`/api/calculator/post-nuestros?p=${P}&h=${H}`)
            .then((r) => { if (!cancelado) setNuestros(r.nuestros || []); })
            .catch(() => { if (!cancelado) setNuestros([]); });
        api('/api/calculator/library-menus', {
            method: 'POST',
            body: JSON.stringify({ mealKey: 'Post', macros_objetivo: { P, H, G: 0 }, orden: 'usado', limit: 30, momento: 'peri' }),
        }).then((r) => {
            if (cancelado) return;
            setOtros((r.menus || []).filter((m) => (m.items || []).length).map((m, i) => ({
                id: m.biblioteca_id || `otro-${i}`,
                titulo: (m.items || []).map((a) => a.nombre).slice(0, 3).join(' · '),
                // «Usado 34 veces», sin autor (31-10). Leía `m.clientes`, que la biblioteca
                // no devuelve (manda `veces` y `personas`): el contador no salía nunca.
                descripcion: m.veces ? `Usado ${m.veces} ${m.veces === 1 ? 'vez' : 'veces'}` : 'De otros usuarios',
                alimentos: (m.items || []).map((a) => ({ ...a, cantidad_g: a.cantidad_g })),
                macros: m.macros_metodo || m.macros || {},
            })));
        }).catch(() => { if (!cancelado) setOtros([]); });
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, objetivo?.P, objetivo?.H]);

    if (!open) return null;
    // «Una cucharadita», «al gusto» y «fijo» viajan con el alimento (se perdían al pulsar «Usar»).
    const usar = (r) => onUsar(r.alimentos.map((a) => ({
        alimento_id: a.alimento_id, nombre: a.nombre, cantidad_g: a.cantidad_g,
        categorias: a.categorias, racion: a.racion || 100, unidades: !!a.unidades,
        por_unidad: !!a.unidades, macros_efectivos: a.macros_efectivos, es_liquido: !!a.es_liquido,
        // Con su enlace, para que la tarjeta lo abra ya sin recargar (Jesús, 27-09 22:44).
        url: a.url || null,
        ...conCantidadConNombre(a),
    })), r.titulo);

    return (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog" aria-modal="true" aria-labelledby="post-sugeridos-titulo"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="post-sugeridos">
            <div className="w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-card border border-border shadow-2xl p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 id="post-sugeridos-titulo" className="font-heading text-xl font-bold text-foreground">Te sugerimos</h2>
                    <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand mb-2">Los nuestros</p>
                {!nuestros ? <p className="text-sm text-muted-foreground py-4">Cargando…</p> : (
                    <div className="space-y-2 mb-5" data-testid="post-nuestros">
                        {(todosNuestros ? nuestros : nuestros.slice(0, NUESTROS_A_LA_VISTA)).map((r) => <Receta key={r.id} r={r} onUsar={usar} />)}
                        {nuestros.length > NUESTROS_A_LA_VISTA && !todosNuestros && (
                            <button type="button" onClick={() => setTodosNuestros(true)} data-testid="post-nuestros-mas"
                                className="w-full py-2 rounded-lg border border-border text-xs font-semibold text-brand hover:border-brand/50">
                                Mostrar más ({nuestros.length - NUESTROS_A_LA_VISTA})
                            </button>
                        )}
                    </div>
                )}
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">De otros usuarios</p>
                {!otros ? <p className="text-sm text-muted-foreground py-4">Cargando…</p> : otros.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">Todavía no hay postentrenos de otros usuarios que te cuadren.</p>
                ) : (
                    <div className="space-y-2" data-testid="post-otros">
                        {(todos ? otros : otros.slice(0, 3)).map((r) => <Receta key={r.id} r={r} onUsar={usar} />)}
                        {otros.length > 3 && !todos && (
                            <button type="button" onClick={() => setTodos(true)} className="text-xs font-semibold text-brand">
                                Ver los {otros.length === 30 ? 'treinta' : otros.length} ›
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
